import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowRight, GraduationCap, MapPin, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const TENANT_CONTEXT_KEY = "medadmits_tenant_slug";

/**
 * Public per-tenant landing page.
 * Reads branding from /api/public/branding/{slug} and renders
 * a clean, fully-branded mini-landing that funnels to /register.
 */
export default function TenantLandingPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [branding, setBranding] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | notfound

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/public/branding/${encodeURIComponent(slug)}`);
        if (cancelled) return;
        setBranding(r.data);
        // Stash slug so /register knows which tenant the lead belongs to
        try {
          sessionStorage.setItem(TENANT_CONTEXT_KEY, slug);
        } catch {
          /* ignore */
        }
        setStatus("ok");

        // Set favicon if provided
        if (r.data.favicon) {
          let link = document.querySelector("link[rel='icon']");
          if (!link) {
            link = document.createElement("link");
            link.rel = "icon";
            document.head.appendChild(link);
          }
          link.href = r.data.favicon;
        }
        document.title = `${r.data.display_name} — Scholarship Test`;
      } catch (e) {
        if (cancelled) return;
        setStatus("notfound");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
        <div className="h-6 w-6 rounded-full border-2 border-[#C9A961] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (status === "notfound" || !branding) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
        <div className="text-center max-w-md p-6">
          <div className="text-5xl">🤔</div>
          <h1 className="mt-4 text-2xl font-bold text-[#0A2540]">
            Tenant not found
          </h1>
          <p className="mt-2 text-slate-600 text-sm">
            <code className="bg-white px-1.5 py-0.5 rounded">/t/{slug}</code> doesn't
            match a registered consultancy.
          </p>
          <Button onClick={() => nav("/")} className="mt-5 bg-[#0A2540]">
            Go to MedAdmits
          </Button>
        </div>
      </div>
    );
  }

  const primary = branding.primary_color || "#0A2540";
  const accent = branding.accent_color || "#C9A961";
  const hero =
    branding.hero_headline ||
    `Score scholarships before NEET results — with ${branding.display_name}.`;
  const heroSub =
    branding.hero_sub ||
    "Take the 10-minute scholarship test and unlock up to 50% off MBBS tuition at top universities abroad.";
  const countries =
    Array.isArray(branding.country_focus) && branding.country_focus.length
      ? branding.country_focus
      : ["Russia", "Georgia", "Uzbekistan", "Kyrgyzstan"];

  return (
    <div
      className="min-h-screen bg-[#FAF9F6]"
      data-testid="tenant-landing"
      style={{
        // CSS variables drive the brand-color CTAs
        "--brand-primary": primary,
        "--brand-accent": accent,
      }}
    >
      {/* Top bar */}
      <header className="border-b border-slate-200/60 bg-white/80 backdrop-blur sticky top-0 z-30">
        <div className="mx-auto max-w-6xl px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3" data-testid="tenant-logo-row">
            {branding.logo ? (
              <img
                src={branding.logo}
                alt={branding.display_name}
                className="h-9 w-9 rounded-md object-contain bg-white border border-slate-100"
              />
            ) : (
              <div
                className="h-9 w-9 rounded-md flex items-center justify-center font-display text-white font-bold"
                style={{ backgroundColor: primary }}
              >
                {(branding.display_name || "M").charAt(0)}
              </div>
            )}
            <div>
              <div
                className="font-display font-bold leading-tight"
                style={{ color: primary }}
              >
                {branding.display_name}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">
                MBBS Scholarships · Admissions
              </div>
            </div>
          </div>
          <Link
            to="/register"
            data-testid="tenant-nav-cta"
            style={{ backgroundColor: primary }}
            className="hidden sm:inline-flex items-center gap-1 text-white px-4 py-2 rounded-full text-sm font-medium hover:opacity-90"
          >
            Take the test <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-6xl px-5 pt-12 pb-16 lg:pt-20 lg:pb-24">
        <div
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] mb-5"
          style={{ color: accent }}
        >
          <span className="h-px w-8" style={{ backgroundColor: accent, opacity: 0.6 }} />
          {branding.tagline || "Scholarships · MBBS Abroad"}
        </div>

        <h1
          className="font-display font-bold text-[40px] sm:text-[56px] lg:text-[64px] leading-[1.04] tracking-tight max-w-4xl"
          style={{ color: primary }}
          data-testid="tenant-hero-headline"
        >
          {hero}
        </h1>

        <p
          className="mt-6 max-w-2xl text-[17px] sm:text-[19px] leading-relaxed text-slate-700"
          data-testid="tenant-hero-sub"
        >
          {heroSub}
        </p>

        <div className="mt-10 flex flex-col sm:flex-row gap-3">
          <Link
            to="/register"
            data-testid="tenant-primary-cta"
            style={{ backgroundColor: primary }}
            className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full text-sm font-medium text-white hover:opacity-90 transition"
          >
            <Sparkles className="h-4 w-4" />
            Take the scholarship test
            <ArrowRight className="h-4 w-4" />
          </Link>
          <a
            href="#how"
            className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full text-sm font-medium border-2"
            style={{ color: primary, borderColor: `${primary}30` }}
          >
            How it works
          </a>
        </div>

        <div className="mt-8 flex items-center gap-2 text-xs text-slate-600">
          <GraduationCap className="h-4 w-4" style={{ color: accent }} />
          Free · 10 min · Results delivered on WhatsApp & email
        </div>
      </section>

      {/* Country focus cards */}
      <section id="how" className="bg-white border-y border-slate-100">
        <div className="mx-auto max-w-6xl px-5 py-16">
          <div className="text-[11px] uppercase tracking-[0.22em] mb-3" style={{ color: accent }}>
            Where we place students
          </div>
          <h2
            className="font-display font-bold text-3xl sm:text-4xl max-w-2xl mb-10"
            style={{ color: primary }}
          >
            Top universities, scholarship slabs included.
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {countries.map((c) => (
              <div
                key={c}
                className="rounded-xl border border-slate-200 bg-[#FAF9F6] p-5 hover:shadow-md transition-shadow"
                data-testid={`country-card-${c.toLowerCase()}`}
              >
                <MapPin className="h-5 w-5 mb-3" style={{ color: accent }} />
                <div className="font-display font-bold text-lg" style={{ color: primary }}>
                  {c}
                </div>
                <div className="mt-1 text-xs text-slate-600">
                  Up to 50% scholarships · NMC approved
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA banner */}
      <section className="mx-auto max-w-6xl px-5 py-20 text-center">
        <h3
          className="font-display font-bold text-3xl sm:text-4xl max-w-3xl mx-auto"
          style={{ color: primary }}
        >
          Ready to see your scholarship slab?
        </h3>
        <p className="mt-4 text-slate-600 max-w-xl mx-auto">
          10 minutes. Real questions. Real scholarships from real universities.
        </p>
        <Link
          to="/register"
          style={{ backgroundColor: primary }}
          className="mt-8 inline-flex items-center gap-2 px-8 py-4 rounded-full text-white font-medium hover:opacity-90"
        >
          Take the test now <ArrowRight className="h-4 w-4" />
        </Link>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-5 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <div>© 2026 {branding.display_name}</div>
          <div className="text-[10px] uppercase tracking-wider">
            Powered by <span style={{ color: accent }}>MedAdmits</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Helper for /register etc to read the active tenant slug (if visitor came via /t/:slug)
export function getActiveTenantSlug() {
  try {
    return sessionStorage.getItem(TENANT_CONTEXT_KEY) || null;
  } catch {
    return null;
  }
}
